import { useState, useEffect, useContext } from "react";
import { useLocalization } from "@fluent/react";
import { Select, Button } from "@narmi/design_system";
import { useTriggerGuideForceReset } from "../../src/modules/guides/hooks";
import AccountContext from "../contexts/AccountContext";
import ComboAccountSelector from "../ComboAccountSelector";
import {
  downloadTransactionsForPastYear,
  FileFormat,
} from "./transactionDownloadHelpers";
import styles from "./DownloadTransactionsDialog.module.scss";

import FILE_FORMATS from "./constants";

interface DownloadTransactionsDialogProps {
  closeDialog: () => void;
}

const DownloadTransactionsDialog = ({
  closeDialog,
}: DownloadTransactionsDialogProps) => {
  const { l10n } = useLocalization();
  const { internalAccounts } = useContext(AccountContext);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [selectedFormat, setSelectedFormat] = useState<FileFormat>("qbo");
  const [isDownloading, setIsDownloading] = useState(false);
  const triggerGuideForceReset = useTriggerGuideForceReset();

  // Set default account when accounts are available
  useEffect(() => {
    if (internalAccounts && internalAccounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(internalAccounts[0].id);
    }
  }, [internalAccounts, selectedAccountId]);

  const handleDownload = async () => {
    if (!selectedAccountId) {
      return;
    }

    setIsDownloading(true);

    try {
      await downloadTransactionsForPastYear(selectedAccountId, selectedFormat);
      triggerGuideForceReset();
      closeDialog();
    } catch (error) {
      // Error handled silently - user will see any system error messages
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        {l10n.getString(
          "description-download-transactions",
          null,
          "Download your transactions from any account to upload and sync your information with your accounting software."
        )}
      </p>

      <div className={styles.selectorGroup}>
        <ComboAccountSelector
          value={selectedAccountId}
          accounts={internalAccounts}
          isDestination={false}
          label={l10n.getString("label-account", null, "Account")}
          onChange={setSelectedAccountId}
          showAddExternalAccountLink={false}
        />

        <Select
          label={l10n.getString("label-file-format", null, "File format")}
          value={selectedFormat}
          onChange={(value: string) => setSelectedFormat(value as FileFormat)}
          disabled={isDownloading}
        >
          {FILE_FORMATS.map((format) => (
            <Select.Item key={format.value} value={format.value}>
              {format.displayName}
            </Select.Item>
          ))}
        </Select>
      </div>

      <div className={styles.buttonContainer}>
        <Button
          onClick={handleDownload}
          disabled={!selectedAccountId || isDownloading}
          testId="download-transactions"
        >
          {isDownloading
            ? l10n.getString("button-downloading", null, "Downloading...")
            : l10n.getString("button-download", null, "Download")}
        </Button>
      </div>
    </div>
  );
};

export default DownloadTransactionsDialog;
